import React, { useState } from 'react';

import { useEventsActions } from './EventsContext';
import { createEvent } from './restApi';
import Form from '../../Shared/UI/Form/components/Form';
import Field from '../../Shared/UI/Field/components/Field';
import Input from '../../Shared/UI/Input/components/Input';
import Textarea from '../../Shared/UI/Textarea/components/Textarea';
import Select from '../../Shared/UI/Select/components/Select';
import FileInput from '../../Shared/UI/FileInput/components/FileInput';
import FormActions from '../../Shared/UI/FormActions/components/FormActions';
import Button from '../../Shared/UI/Button/components/Button';
import ResponsiveContainer from '../../Shared/UI/ResponsiveContainer/components/ResponsiveContainer';

const EventForm = ({ timezones }) => {
  const { setEventWizardOpen, onError } = useEventsActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    location: '',
    image: null,
    imageFileName: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    timezoneName: timezones?.[0]?.name || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const base64Image = (image) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result
        .replace("data", "")
        .replace(/^.+,/, "");

      setFormData(prev => ({
        ...prev,
        image: base64String,
        imageFileName: image.name,
      }));
    };

    reader.readAsDataURL(image);
  };

  const parseDate = (dateString) => {
    if (!dateString) return { year: '', month: '', day: '' };
    const [year, month, day] = dateString.split('-');
    return { year: parseInt(year, 10), month: parseInt(month, 10), day: parseInt(day, 10) };
  };

  const parseTime = (timeString) => {
    if (!timeString) return { hour: '', minute: '' };
    const [hour, minute] = timeString.split(':');
    return { hour: parseInt(hour, 10), minute: parseInt(minute, 10) };
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    const startDate = parseDate(formData.startDate);
    const startTime = parseTime(formData.startTime);
    const endDate = parseDate(formData.endDate);
    const endTime = parseTime(formData.endTime);

    createEvent({
      onResponse: (response) => {
        setIsSubmitting(false);
        if (response.feedbackVariant === 'success') {
          setEventWizardOpen(false);
          localStorage.setItem('feedbackMessage', response.feedbackMessage);
          localStorage.setItem('feedbackVariant', response.feedbackVariant);
          window.location.href = `/events/${response.eventId}`;
        } else {
          onError({
            feedbackMessage: response.feedbackMessage || 'Failed to create event',
            feedbackVariant: response.feedbackVariant || 'error',
          });
        }
      },
      onError: (feedback) => {
        setIsSubmitting(false);
        onError(feedback);
      },
      body: {
        authenticityToken,
        event: {
          title: formData.title,
          shortDescription: formData.shortDescription,
          location: formData.location,
          image: formData.image,
          imageFileName: formData.imageFileName,
          startYear: startDate.year,
          startMonth: startDate.month,
          startDay: startDate.day,
          startHour: startTime.hour,
          startMinute: startTime.minute,
          endYear: endDate.year,
          endMonth: endDate.month,
          endDay: endDate.day,
          endHour: endTime.hour,
          endMinute: endTime.minute,
          timezoneName: formData.timezoneName,
        },
      },
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Field label="Event Image" hint="Optional" id="event_image">
        <FileInput
          accept="image/png,image/gif,image/jpeg"
          onChange={(e) => {
            if (e.target.files[0]) {
              base64Image(e.target.files[0]);
            }
          }}
        />
      </Field>

      <Field label="Title" id="event_title">
        <Input
          type="text"
          value={formData.title}
          onChange={(e) => updateField('title', e.target.value)}
        />
      </Field>

      <Field label="Short Description" id="event_short_description">
        <Textarea
          value={formData.shortDescription}
          onChange={(e) => updateField('shortDescription', e.target.value)}
        />
      </Field>

      <Field label="Location" id="event_location">
        <Input
          type="text"
          value={formData.location}
          onChange={(e) => updateField('location', e.target.value)}
        />
      </Field>

      <Field label="Start Date" id="event_start_date">
        <Input
          type="date"
          value={formData.startDate}
          onChange={(e) => updateField('startDate', e.target.value)}
        />
      </Field>

      <Field label="Start Time" id="event_start_time">
        <Input
          type="time"
          value={formData.startTime}
          onChange={(e) => updateField('startTime', e.target.value)}
        />
      </Field>

      <Field label="End Date" id="event_end_date">
        <Input
          type="date"
          value={formData.endDate}
          onChange={(e) => updateField('endDate', e.target.value)}
        />
      </Field>

      <Field label="End Time" id="event_end_time">
        <Input
          type="time"
          value={formData.endTime}
          onChange={(e) => updateField('endTime', e.target.value)}
        />
      </Field>

      <Field label="Timezone" id="event_timezone">
        <Select
          value={formData.timezoneName}
          onChange={(e) => updateField('timezoneName', e.target.value)}
        >
          {(timezones || []).map((tz) => (
            <option key={tz.name} value={tz.name}>
              {tz.displayName || tz.name}
            </option>
          ))}
        </Select>
      </Field>

      <ResponsiveContainer name="form-actions">
        <FormActions>
          <Button
            variant="secondary"
            onClick={() => setEventWizardOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Event'}
          </Button>
        </FormActions>
      </ResponsiveContainer>
    </Form>
  );
};

export default EventForm;
